import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { comprasAPI } from '../api/client';
import { ShoppingBag, Plus, Search, Calendar, DollarSign, Package } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';

const ComprasPage = () => {
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const { formatCurrency } = useConfig();

  useEffect(() => {
    loadCompras();
  }, []);

  const loadCompras = async () => {
    try {
      const response = await comprasAPI.getAll();
      if (response.data.success) {
        setCompras(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar compras:', error);
      setCompras([]);
    } finally {
      setLoading(false);
    }
  };

  const comprasFiltradas = compras.filter(compra =>
    compra.folio?.toLowerCase().includes(busqueda.toLowerCase()) ||
    compra.proveedor_nombre?.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Compras</h1>
        <button 
          onClick={() => navigate('/compras/nueva')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva Compra
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Compras Mes</p>
              <p className="text-2xl font-bold text-primary-600">
                {formatCurrency(compras.reduce((sum, c) => sum + (c.total || 0), 0))}
              </p>
            </div>
            <ShoppingBag className="w-12 h-12 text-primary-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compras Realizadas</p>
              <p className="text-2xl font-bold text-green-600">{compras.length}</p>
            </div>
            <Package className="w-12 h-12 text-green-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Promedio por Compra</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(compras.length > 0 ? compras.reduce((sum, c) => sum + (c.total || 0), 0) / compras.length : 0)}
              </p>
            </div>
            <DollarSign className="w-12 h-12 text-blue-600 opacity-20" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por folio o proveedor..."
              className="input-field pl-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : comprasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    {busqueda ? 'No se encontraron compras' : 'No hay compras registradas'}
                  </td>
                </tr>
              ) : (
                comprasFiltradas.map((compra) => (
                  <tr key={compra.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{compra.folio}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(compra.fecha).toLocaleDateString('es-GT')}
                      </div>
                    </td>
                    <td className="px-6 py-4">{compra.proveedor_nombre || '-'}</td>
                    <td className="px-6 py-4 font-bold text-primary-600">
                      {formatCurrency(compra.total)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded bg-green-100 text-green-800">
                        Completada
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComprasPage;
